/**
 * PagePermsContext — exposes read/write/update permissions for the current page.
 * Usage inside any page component:
 *   const { canWrite, canUpdate } = usePagePerms();
 *   {canWrite && <button>+ New Task</button>}
 */
import { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { usePageAccess } from './hooks/usePermissions';

// Permissive by default: a page rendered outside the provider must keep
// working, not silently lose its buttons.
const PagePermsCtx = createContext({ canRead:true, canWrite:true, canUpdate:true, loading:false });

export function PagePermsProvider({ children }) {
  const { user }     = useAuth();
  const { pathname } = useLocation();
  const { pagePerms, loading } = usePageAccess(user?.id);

  // /tasks/123 resolves to /tasks. The root path has no first segment, so
  // the || must be inside the concat or '/' would win for every page.
  const first    = pathname.split('/').filter(Boolean)[0];
  const basePath = first ? `/${first}` : '/';
  const perms    = pagePerms?.[pathname] || pagePerms?.[basePath] || null;

  // While the fetch is in flight, pagePerms is {} and every page would look
  // read-only for a moment — buttons appearing a second after the page does.
  // Treat "not loaded yet" as permitted and let the real answer narrow it.
  const unknown = loading || !perms;

  const value = useMemo(() => ({
    loading,
    canRead:   unknown ? true : perms.read   !== false,
    canWrite:  unknown ? true : perms.write  === true,
    canUpdate: unknown ? true : perms.update === true,
  }), [loading, unknown, perms?.read, perms?.write, perms?.update]);

  return <PagePermsCtx.Provider value={value}>{children}</PagePermsCtx.Provider>;
}

export function usePagePerms() {
  return useContext(PagePermsCtx);
}
